import React, { useMemo, useRef } from 'react'

import classNames from 'clsx'
import color from 'color'
import memoize from 'memoize-one'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import {
  Badge,
  ButtonBase,
  ListItem,
  Tooltip as MUITooltip,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import AddButton from '~/src/Annotations/AddButton'
import { Allowed, pickAllowedProps } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import {
  EMPTY_ARRAY,
  goldenth,
  slugify,
  useBreakpoint,
  useIsBreakpointDown,
} from '~/src/Lib/Utils'
import { roomOptionSort } from '~/src/Room/utils'
import SharedIcon from '~/src/UI/Shared/Icon'

const displayName = 'Nav'
const logger = createLogger(displayName)
// eslint-disable-next-line react/prop-types
const Tooltip = ({ label, drawerOpen, anchorRef, ...props }) => (drawerOpen ? (
  // eslint-disable-next-line react/jsx-no-useless-fragment
  <React.Fragment {...props} />
) : (
  <MUITooltip
    {...props}
    title={label}
    label={label}
    placement="right"
    PopperProps={{
      anchorEl: () => anchorRef && anchorRef.current,
      modifiers: [{
        name: 'flip',
        enabled: false
      }, {
        name: 'offset',
        options: { offset: [0, -16] }
      }],
    }}
  />
))
Tooltip.displayName = `${displayName}/Tooltip`
Tooltip.propTypes = {
  anchorRef: PropTypes.shape({
    current: (props, name, component, path) => {
      const prop = props[name]
      if (prop == null || prop?.nodeType === 1) {
        return undefined
      }
      return new Error(`Tooltip received invalid prop ${path}: ${prop}`)
    },
  }).isRequired,
}

const styles = theme => ({
  root: {
    display: 'flex',
    width: 72,
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1.5),
    height: '100%',
    zIndex: theme.zIndex.drawer + 1,
  },
  navItems: {
    flex: '0 1 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'visible auto',
    paddingBottom: theme.spacing(),
    scrollBehavior: 'smooth',
    '&::-webkit-scrollbar': {
      height: 0,
    },
    '@media (max-height: 800px)': {
      paddingLeft: '6px !important',
    },
  },
  logoIcon: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
  },
  item: {
    padding: theme.spacing(2, 3),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    '& [class*=MuiAvatar-root]': {
      width: '1.5rem',
      height: '1.5rem',
      '& img': {
        width: '1.5rem',
        height: '1.5rem',
      }
    },
    '&$selected:after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '90%',
      height: '100%',
      borderRadius: 5,
      background: color(theme.palette.primary.main).alpha(goldenth(4)).string()
    },
  },
  addButton: {
    overflow: 'visible !important',
    zIndex: 10,
    margin: theme.spacing(1, 0),
    position: 'relative',
    transition: 'filter 250ms ease-in-out',
    '& [class*=MuiSpeedDial-actions]': {
      position: 'fixed',
      bottom: 'auto',
      left: 0,
      transform: 'translate(-20px, 50%)',
      padding: theme.spacing(0, 10, 0, 11),
      margin: theme.spacing(0, 0, 2, 0),
      '@media screen and (max-height: 800px)': {
        transform: 'translate(-20px, 10px)',
        margin: 0,
      },
    },
  },
  otherItems: {
    flex: '0 1 auto',
  },
  selected: {},
  footer: {
    marginTop: theme.spacing(9),
    paddingBottom: theme.spacing(1)
  },
})

const urlMatches = (current, other) => current.indexOf(other.replace(/\?.*$/, '')) === 0

const urlIsCurrent = (url = '', currentURL = '', urlList = EMPTY_ARRAY) => (
  urlMatches(currentURL, url) || urlList.some(listUrl => urlMatches(currentURL, listUrl))
)

const getLinkItem = memoize(
  ({ classes, currentURL, drawerOpen, navBadgesByUrl, onLinkClick }) => props => {
    const {
      label,
      Icon,
      url,
      urlList = EMPTY_ARRAY,
      onClick,
    } = props

    const anchorRef = useRef(null)
    const isIcon = Boolean(Icon)
    const allowedProps = pickAllowedProps(props)

    const badge = navBadgesByUrl[url]
    const hasBadge = Boolean(badge)

    return (
      <Allowed {...allowedProps}>
        <Tooltip label={label} drawerOpen={drawerOpen} anchorRef={anchorRef}>
          <ButtonBase
            onClick={onClick ?? onLinkClick}
            href={url}
            className={classNames({
              [classes.item]: true,
              [classes.selected]: urlIsCurrent(url, currentURL, urlList),
            })}
            data-testid={`aroya-nav-${slugify(label)}`}
            ref={anchorRef}
          >
            {isIcon && hasBadge ? (
              <Badge
                color={badge.color ?? 'error'}
                badgeContent={badge.value}
                variant={badge.value ? undefined : 'dot'}
              >
                <SharedIcon icon={Icon} />
              </Badge>
            ) : null}
            {isIcon && !hasBadge ? (
              <SharedIcon icon={Icon} />
            ) : null}
          </ButtonBase>
        </Tooltip>
      </Allowed>
    )
  }
)

export class NavComponent extends React.PureComponent {
  static displayName = displayName

  static propTypes = {
    theme: PropTypes.shape({ direction: PropTypes.oneOf(['ltr', 'rtl']) })
      .isRequired,
    authenticated: PropTypes.bool.isRequired,
    classes: PropTypes.objectOf(PropTypes.string).isRequired,
    className: PropTypes.string.isRequired,
    avatarURL: PropTypes.string,
    currentURL: PropTypes.string.isRequired,
    drawerOpen: PropTypes.bool.isRequired,
    drawerToggle: PropTypes.func.isRequired,
    isLoggingOut: PropTypes.bool.isRequired,
    navBadgesByUrl: PropTypes.objectOf(PropTypes.shape({
      color: PropTypes.string,
      value: PropTypes.number,
    })).isRequired,
    outdoor: PropTypes.bool.isRequired,
    rooms: PropTypes.arrayOf(PropTypes.number).isRequired,
    routes: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    })).isRequired,
    app: PropTypes.string.isRequired,
  }

  static defaultProps = {
    avatarURL: null,
  }

  // logoutRef = React.createRef()

  onLinkClick = event => {
    const { currentTarget } = event
    const target = currentTarget.href
      ? currentTarget
      : currentTarget.closest('a[href]')

    if (!target || !target.href) return

    const { drawerOpen, drawerToggle } = this.props
    // eslint-disable-next-line no-restricted-globals
    if (target.href !== location.href && drawerOpen) {
      drawerToggle()
    }
  }

  render() {
    const {
      authenticated,
      classes,
      className,
      currentURL,
      drawerOpen,
      navBadgesByUrl,
      routes,
      isLoggingOut,
      app,
    } = this.props
    const { onLinkClick } = this

    const LinkItem = getLinkItem({
      classes,
      currentURL,
      drawerOpen,
      navBadgesByUrl,
      onLinkClick,
    })

    return (
      <nav
        className={classNames(classes.root, className)}
        data-testid="aroya-main-nav"
        data-trackwidth="navRail"
      >
        <div className={classes.navItems}>
          <ListItem
            key="Home"
            label="Home"
            className={classes.logoIcon}
          >
            <img src={`/images/${app}-logo.svg`} style={{ backgroundColor: 'white' }} alt="logo" width="50px" />
          </ListItem>
          {routes.map(route => {
            const label = typeof route.label === 'function' ? route.label(this.props) : route.label
            const url = typeof route.getURL === 'function' ? route.getURL(this.props) : route.url
            return (
              <LinkItem
                {...route}
                key={label}
                label={label}
                url={url}
              />
            )
          })}
          {!authenticated || isLoggingOut ? null : (
            <div
              id="add-annotation-anchor"
              className={classes.addButton}
            >
              <AddButton
                openOnHover
                tooltipOpen
                direction="up"
                opener="nav"
                tooltipPlacement="right"
              />
            </div>
          )}
        </div>
      </nav>
    )
  }
}

const useStyles = makeStyles(styles, { name: displayName })

const Memoized = React.memo(props => {
  const {
    activeRooms,
    currentFacility,
    meRaw,
    navBadgesByUrl,
    routes,
  } = useConnect(
    'selectActiveRooms',
    'selectCurrentFacility',
    'selectMeRaw',
    'selectNavBadgesByUrl',
    'selectRoutes',
  )
  const theme = useTheme()
  const classes = useStyles(props)
  const breakpoint = useBreakpoint()
  const isSmDown = useIsBreakpointDown('sm', breakpoint)
  const navRoutes = useMemo(() => {
    const routeList = Object.entries(routes).filter(([, config]) => Boolean(config.nav))
      .map(([url, { nav, title, ...other }]) => ({
        ...nav,
        label: nav?.label ?? title,
        url,
        ...other
      })).sort((a, b) => a.sequence - b.sequence)
    logger.debug('[routeList]:', routeList)
    return routeList
  }, [routes])
  const roomIds = useMemo(
    () => roomOptionSort(Object.values(activeRooms)).map(({ id }) => id),
    [activeRooms]
  )

  return meRaw.lastSuccess && currentFacility ? (
    <NavComponent
      {...props}
      breakpoint={breakpoint}
      classes={classes}
      navBadgesByUrl={navBadgesByUrl}
      isSmDown={isSmDown}
      app={(currentFacility.app ?? '').toLowerCase()}
      outdoor={currentFacility.outdoor}
      rooms={roomIds}
      routes={navRoutes}
      theme={theme}
    />
  ) : null
})

export default Memoized
